// PhoneProfileRow — renders a "Phone" row that visually continues the
// WorkOS UserProfile widget above it. The outer bordered container
// (with margin-top: -1px to overlap the widget's bottom border and a
// rounded bottom-only radius) keeps the "merged" look the widget has.
// The inner row uses Radix Themes DataList primitives so the label
// column width, text size and min-height come from the same tokens the
// WorkOS widget consumes — no hardcoded pixels to drift out of sync.
//
// Requires an ancestor <Theme> provider for Radix Themes tokens to
// resolve. Admin pages inherit Theme from the app root via <Card>; the
// engineer profile page wraps this component in <Theme> explicitly.

import { useState } from 'react'
import { Button, DataList, Flex, Strong, Text } from '@radix-ui/themes'
import { useMe } from '../../auth/hooks/useMe'
import { PhoneEditModal } from './PhoneEditModal'

export function PhoneProfileRow() {
  const { data: meData } = useMe()
  const [open, setOpen] = useState(false)

  const phone = meData?.phone || ''
  const isSsoManaged = meData?.is_sso_managed ?? false

  return (
    <>
      <div className="phone-profile-container">
        <DataList.Root
          size="2"
          orientation="horizontal"
          className="phone-profile-datalist"
        >
          <DataList.Item align="center">
            <DataList.Label minWidth="120px" highContrast>
              <Strong>Phone</Strong>
            </DataList.Label>
            <DataList.Value>
              <Flex justify="between" align="center" gap="3" width="100%">
                <Text color={phone ? undefined : 'gray'}>
                  {phone || 'Not set'}
                </Text>
                {isSsoManaged ? (
                  <Text size="1" color="gray">
                    Managed by directory
                  </Text>
                ) : (
                  <Button
                    color="gray"
                    variant="surface"
                    size="2"
                    onClick={() => setOpen(true)}
                    style={{ color: 'var(--gray-12)' }}
                  >
                    Edit
                  </Button>
                )}
              </Flex>
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </div>

      <PhoneEditModal
        open={open}
        initialValue={phone}
        onClose={() => setOpen(false)}
      />

      <style>{`
        /* Outer container mirrors the WorkOS widget's row card so the
           phone row reads as a continuation of the widget above. The
           margin-top: -1px overlaps the widget's bottom border for a
           seamless join. */
        .phone-profile-container {
          border: 1px solid var(--gray-a5, #e5e7eb);
          border-top: none;
          border-radius: 0 0 8px 8px;
          background: #fff;
          margin-top: -1px;
          /* Match the WorkOS widget's card padding (--card-padding =
             --space-4 = 16px) so the Edit button sits the same 16px
             from the right edge as the widget's Edit buttons above. */
          padding: 16px;
          overflow: hidden;
        }
      `}</style>
    </>
  )
}
